// Reusable content drawer: slides in from the right on desktop, up from the
// bottom (sheet) on mobile. Positioning + animation live in base.css, keyed on
// the `is-open` class this component toggles.
//
// Open from anywhere with a trigger:  <button data-drawer-trigger="my-id">
// and a content source:  <template data-drawer-content="my-id" data-drawer-title="Title">…</template>

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class ContentDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector("[data-drawer-overlay]");
    this.panel = this.querySelector("[data-drawer-panel]");
    this.titleTarget = this.querySelector("[data-drawer-title-target]");
    this.body = this.querySelector("[data-drawer-body]");
    this.closeButton = this.querySelector("[data-drawer-close]");
    this.isOpen = false;
    this.lastFocused = null;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener("click", (event) => {
      if (
        event.target.closest("[data-drawer-close]") ||
        event.target === this.overlay
      ) {
        this.close();
      }
    });

    document.addEventListener("click", this.onTriggerClick);
    document.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onTriggerClick);
    document.removeEventListener("keydown", this.onKeydown);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest("[data-drawer-trigger]");
    if (!trigger) return;
    event.preventDefault();
    this.openFrom(trigger);
  }

  onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  openFrom(trigger) {
    const id = trigger.getAttribute("data-drawer-trigger");
    const template = document.querySelector(
      `template[data-drawer-content="${CSS.escape(id)}"]`,
    );
    if (!template) return;

    this.lastFocused = trigger;

    const title = template.getAttribute("data-drawer-title") ?? "";
    if (this.titleTarget) {
      this.titleTarget.textContent = title;
      this.titleTarget.classList.toggle("hidden", title.trim() === "");
    }

    // A drawer opened without a title has nothing for aria-labelledby to point
    // at, leaving the dialog unnamed — fall back to the trigger's own label.
    if (title.trim() === "") {
      this.panel?.setAttribute("aria-label", trigger.textContent.trim());
    } else {
      this.panel?.removeAttribute("aria-label");
    }

    this.body?.replaceChildren(template.content.cloneNode(true));
    this.open();
  }

  open() {
    this.isOpen = true;
    this.classList.remove("hidden");
    this.setAttribute("aria-hidden", "false");
    document.body.classList.add("overflow-hidden");

    // Double rAF so the off-screen state paints before the slide-in transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add("is-open"));
    });

    // preventScroll: the panel is still parked off-viewport (translateX(100%))
    // at this point, so a plain focus() makes the browser scroll the page to
    // "reveal" it — the article behind the drawer jumps.
    this.closeButton?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove("is-open");
    this.setAttribute("aria-hidden", "true");

    setTimeout(() => {
      this.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
    }, 800); // keep in sync with --drawer-duration in base.css

    this.lastFocused?.focus({ preventScroll: true });
  }

  trapFocus(event) {
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("content-drawer")) {
  customElements.define("content-drawer", ContentDrawer);
}
